import { useAuth } from "@clerk/react";
import {
	createRootRoute,
	Link,
	Navigate,
	Outlet,
	useLocation,
	useNavigate,
} from "@tanstack/react-router";
import {
	Bell,
	CreditCard,
	LayoutGrid,
	ListChecks,
	Monitor,
	Moon,
	Plus,
	Sun,
} from "lucide-react";
import { type ReactNode, useEffect } from "react";

import { FoundationModelSmokeTest } from "@/components/foundation-model-smoke-test";
import { SubscribeScreen } from "@/components/kb/subscribe-screen";
import { Wordmark } from "@/components/kb/wordmark";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { useTheme } from "@/hooks/use-theme";
import { useNotifications } from "@/hooks/useNotifications";
import { useRules } from "@/hooks/useRules";
import { useSubscription } from "@/hooks/useSubscription";
import { postAuthState, subscribeToNotificationOpens } from "@/lib/native";
import { cn } from "@/lib/utils";

const TOKEN_REFRESH_MS = 30_000;

const NAV_ITEM =
	"flex h-9 items-center gap-2 rounded-md px-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground";
const NAV_ACTIVE = "bg-sidebar-accent text-foreground";

function NativeBridge() {
	const { getToken } = useAuth();
	const navigate = useNavigate();

	useEffect(() => {
		let active = true;

		const syncToken = async () => {
			const token = await getToken();

			if (active) {
				postAuthState(token);
			}
		};

		void syncToken();

		const interval = window.setInterval(() => {
			void syncToken();
		}, TOKEN_REFRESH_MS);

		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				void syncToken();
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			active = false;
			window.clearInterval(interval);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [getToken]);

	useEffect(
		() =>
			subscribeToNotificationOpens((notificationId) => {
				navigate({ search: { notificationId }, to: "/" });
			}),
		[navigate],
	);

	return null;
}

function ThemeIcon({ theme }: { theme: "system" | "light" | "dark" }) {
	if (theme === "light") {
		return <Sun />;
	}

	if (theme === "dark") {
		return <Moon />;
	}

	return <Monitor />;
}

function Shell() {
	const pathname = useLocation({ select: (location) => location.pathname });
	const { theme, cycleTheme } = useTheme();
	const notifications = useNotifications();
	const rules = useRules();

	const unreadCount = (notifications.data?.notifications ?? []).filter(
		(notification) => !notification.readAt,
	).length;
	const ruleCount = rules.data?.rules.length ?? 0;

	const isNotifications = pathname === "/";
	const isRules = pathname.startsWith("/rules") && pathname !== "/rules/create";
	const isNewRule = pathname === "/rules/create";
	const isApps = pathname.startsWith("/apps");
	const isBilling = pathname.startsWith("/subscribe");

	return (
		<div className="flex h-dvh overflow-hidden bg-background">
			<aside className="hidden w-60 shrink-0 flex-col border-r bg-sidebar px-3 py-5 md:flex">
				<div className="px-1.5">
					<Link to="/">
						<Wordmark />
					</Link>
				</div>

				<div className="mt-6">
					<Button
						asChild
						className="w-full justify-start"
						variant={isNewRule ? "secondary" : "default"}
					>
						<Link to="/rules/create">
							<Plus />
							New rule
						</Link>
					</Button>
				</div>

				<nav className="mt-5 flex flex-col gap-0.5">
					<Link className={cn(NAV_ITEM, isNotifications && NAV_ACTIVE)} to="/">
						<Bell className="size-4" />
						Notifications
						{unreadCount > 0 && (
							<span className="ml-auto font-display text-sm font-bold text-signal-ink">
								{unreadCount}
							</span>
						)}
					</Link>
					<Link className={cn(NAV_ITEM, isRules && NAV_ACTIVE)} to="/rules">
						<ListChecks className="size-4" />
						Rules
						{ruleCount > 0 && (
							<span className="ml-auto type-data text-muted-foreground">
								{ruleCount}
							</span>
						)}
					</Link>
					<Link className={cn(NAV_ITEM, isApps && NAV_ACTIVE)} to="/apps">
						<LayoutGrid className="size-4" />
						Apps
					</Link>
					<Link
						className={cn(NAV_ITEM, isBilling && NAV_ACTIVE)}
						to="/subscribe"
					>
						<CreditCard className="size-4" />
						Billing
					</Link>
				</nav>

				<div className="mt-auto flex flex-col gap-1 border-t pt-3">
					<button
						className={cn(NAV_ITEM, "w-full")}
						onClick={cycleTheme}
						type="button"
					>
						<ThemeIcon theme={theme} />
						Theme
						<span className="ml-auto text-xs text-muted-foreground capitalize">
							{theme}
						</span>
					</button>
				</div>
			</aside>

			<div className="flex min-w-0 flex-1 flex-col">
				<header
					className="flex h-14 shrink-0 items-center justify-between border-b bg-background/90 px-3 backdrop-blur md:hidden"
					style={{ paddingTop: "env(safe-area-inset-top)" }}
				>
					<Link to="/">
						<Wordmark />
					</Link>
					<div className="flex items-center gap-1">
						<Button asChild size="icon-sm" variant="ghost">
							<Link aria-label="Apps" to="/apps">
								<LayoutGrid />
							</Link>
						</Button>
						<Button asChild size="icon-sm" variant="ghost">
							<Link aria-label="Billing" to="/subscribe">
								<CreditCard />
							</Link>
						</Button>
						<Button
							aria-label="Change theme"
							onClick={cycleTheme}
							size="icon-sm"
							variant="ghost"
						>
							<ThemeIcon theme={theme} />
						</Button>
					</div>
				</header>

				<main
					className="flex-1 overflow-y-auto pb-[calc(7rem+env(safe-area-inset-bottom))] md:pb-0"
					id="main"
				>
					<div className="mx-auto w-full max-w-5xl px-4 py-6 md:px-6 md:py-10">
						<Outlet />
					</div>
				</main>

				<nav
					className="fixed inset-x-0 bottom-0 z-40 grid h-16 grid-cols-3 items-center border-t bg-background/95 backdrop-blur md:hidden"
					style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
				>
					<Link
						className={cn(
							"flex flex-col items-center gap-0.5 text-[0.6875rem] font-medium",
							isNotifications ? "text-foreground" : "text-muted-foreground",
						)}
						to="/"
					>
						<span className="relative">
							<Bell className="size-5" />
							{unreadCount > 0 && (
								<span className="absolute -top-1 -right-1 flex min-w-4 items-center justify-center rounded-full bg-signal px-1 font-display text-[0.625rem] leading-4 font-bold text-signal-foreground">
									{unreadCount}
								</span>
							)}
						</span>
						Alerts
					</Link>
					<Link
						aria-label="New rule"
						className="flex justify-center"
						to="/rules/create"
					>
						<span
							className={cn(
								"flex size-12 -translate-y-3 items-center justify-center rounded-full bg-signal text-signal-foreground shadow-lg ring-4 ring-background",
								isNewRule && "bg-signal/85",
							)}
						>
							<Plus className="size-6" />
						</span>
					</Link>
					<Link
						className={cn(
							"flex flex-col items-center gap-0.5 text-[0.6875rem] font-medium",
							isRules ? "text-foreground" : "text-muted-foreground",
						)}
						to="/rules"
					>
						<ListChecks className="size-5" />
						Rules
					</Link>
				</nav>
			</div>

			<Toaster position="bottom-center" richColors theme={theme} />
		</div>
	);
}

function RootComponent() {
	const { isLoaded, isSignedIn } = useAuth();
	const pathname = useLocation({ select: (location) => location.pathname });
	const subscription = useSubscription();

	let content: ReactNode;

	if (!isLoaded) {
		content = (
			<div className="flex min-h-svh items-center justify-center">
				<p className="type-data text-muted-foreground">Loading…</p>
			</div>
		);
	} else if (!isSignedIn) {
		content = <SubscribeScreen />;
	} else if (subscription.isLoading) {
		content = (
			<div className="flex min-h-svh items-center justify-center">
				<p className="type-data text-muted-foreground">
					Checking your membership…
				</p>
			</div>
		);
	} else if (subscription.error) {
		content = (
			<div className="flex min-h-svh flex-col items-center justify-center gap-4 bg-background p-6 text-center">
				<p className="type-display text-3xl">
					Couldn&apos;t check your membership
				</p>
				<p className="max-w-sm text-sm text-muted-foreground">
					Check your connection and try again.
				</p>
				<Button
					onClick={() => {
						void subscription.refetch();
					}}
					variant="outline"
				>
					Try again
				</Button>
			</div>
		);
	} else if (pathname === "/subscribe") {
		content = <Outlet />;
	} else if (!subscription.active) {
		content = <Navigate replace to="/subscribe" />;
	} else {
		content = <Shell />;
	}

	return (
		<>
			<NativeBridge />
			{content}
			{import.meta.env.DEV ? <FoundationModelSmokeTest /> : null}
		</>
	);
}

export const rootRoute = createRootRoute({
	component: RootComponent,
});
